import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Property {
  id: number;
  title: string;
  price: string; // We'll keep this in the interface for now to maintain compatibility
  location: string;
  area: string;
  image: string;
  dtcpStatus?: 'Approved' | 'Registered' | null;
  reraStatus?: 'Approved' | 'Registered' | null;
  detailsAvailable?: boolean; // New property to control button state
}

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const navigate = useNavigate();
  
  // Function to handle navigation to property detail page
  const handleViewDetails = () => {
    // Only navigate if details are available
    if (property.detailsAvailable !== false) {
      // Convert property title to URL-friendly format
      const formattedTitle = property.title.toLowerCase().replace(/\s+/g, '-');
      
      // Navigate to the appropriate page based on property title
      if (formattedTitle === 'chola-greens') {
        navigate('/projects/chola-greens');
      } else {
        // For other properties, we can create a generic route with the property ID
        // This can be expanded as more property detail pages are created
        navigate(`/properties/${property.id}`);
      }
    }
  };

  return (
    <div className="card hover:shadow-xl transition-shadow duration-300">
      <div className="relative">
        <img
          src={property.image}
          alt={property.title}
          className="w-full h-64 object-cover"
        />
        {/* Price display removed from here */}
        
        {/* Approval Stamps */}
        <div className="absolute bottom-4 left-4 flex flex-col space-y-2">
          {property.dtcpStatus && (
            <div className="stamp-container">
              <div className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs font-bold transform -rotate-12 shadow-lg border-2 border-white relative overflow-hidden">
                <div className="absolute inset-0 bg-green-500 opacity-30 z-0"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <span className="text-xs font-bold tracking-wider">DTCP</span>
                  <span className="text-[10px] font-semibold">{property.dtcpStatus}</span>
                  <div className="absolute top-0 left-0 w-full h-full border-4 border-white border-opacity-20 rounded-lg"></div>
                </div>
              </div>
            </div>
          )}
          {property.reraStatus && (
            <div className="stamp-container mt-1">
              <div className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs font-bold transform -rotate-12 shadow-lg border-2 border-white relative overflow-hidden">
                <div className="absolute inset-0 bg-blue-500 opacity-30 z-0"></div>
                <div className="relative z-10 flex flex-col items-center">
                  <span className="text-xs font-bold tracking-wider">RERA</span>
                  <span className="text-[10px] font-semibold">{property.reraStatus}</span>
                  <div className="absolute top-0 left-0 w-full h-full border-4 border-white border-opacity-20 rounded-lg"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {property.title}
        </h3>
        <p className="text-gray-600 mb-4 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {property.location}
        </p>
        
        <div className="flex justify-center text-sm text-gray-600 mb-4">
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 000 2v9a2 2 0 002 2h6a2 2 0 002-2V6a1 1 0 100-2H3zm2.5 5a.5.5 0 000 1h5a.5.5 0 000-1h-5zm0 2a.5.5 0 000 1h5a.5.5 0 000-1h-5z" clipRule="evenodd" />
            </svg>
            {property.area}
          </span>
        </div>
        
        <button 
          className={`w-full ${property.detailsAvailable === false ? 'bg-gray-400 cursor-not-allowed' : 'btn-primary'}`}
          onClick={handleViewDetails}
          disabled={property.detailsAvailable === false}
        >
          View Details
        </button>
      </div>
    </div>
  );
};

export default PropertyCard;